import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Star, Clock, RefreshCw, Crown, Shield, UserCog, Wrench, Briefcase } from "lucide-react";

// =============================================================================
// LEADERBOARD — surveydrafts.us /api/leaderboard
// Performance ranking by user (avg_rating, jobs_completed, hours, score, rank)
// + per-vendor totals. Pre-computed on SD's side; this page just renders.
// =============================================================================
const ROLE_TONE = {
  finalqc:    "bg-purple-100",
  qaqc:       "bg-indigo-100",
  qaqc1:      "bg-indigo-100",
  qaqc2:      "bg-indigo-100",
  drafter:    "bg-yellow-100",
  fieldcrew:  "bg-sky-100",
  field_crew: "bg-sky-100",
  team_manager: "bg-emerald-100",
  manager:    "bg-emerald-100",
  vendor:     "bg-amber-100",
  admin:      "bg-rose-100",
};

const ROLE_ICON = {
  drafter: UserCog, qaqc: Shield, qaqc1: Shield, qaqc2: Shield, finalqc: Shield,
  field_crew: Wrench, fieldcrew: Wrench,
  team_manager: Crown, manager: Crown, admin: Crown,
  vendor: Briefcase,
};

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("");
  const [filterVendor, setFilterVendor] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/integrations/surveydrafts/leaderboard")
      .then(({ data }) => setData(data))
      .catch(() => setData({ ok: false, leaderboard: [], vendors: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const rows = data?.leaderboard || [];
  const vendors = data?.vendors || [];

  const roles = useMemo(() => {
    const s = new Set(rows.map((r) => (r.role || "").toLowerCase()).filter(Boolean));
    return [...s].sort();
  }, [rows]);

  const vendorOptions = useMemo(() => {
    const s = new Set(rows.map((r) => r.vendor_name).filter(Boolean));
    return [...s].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterRole && (r.role || "").toLowerCase() !== filterRole) return false;
      if (filterVendor && r.vendor_name !== filterVendor) return false;
      if (s && !(r.name || "").toLowerCase().includes(s) && !(r.vendor_name || "").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [rows, filterRole, filterVendor, q]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="leaderboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">SURVEYDRAFTS.US · LIVE RANKING</div>
          <h1 className="font-heading text-4xl font-black tracking-tight leading-none mt-1">Leaderboard</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Pre-computed performance ranking · score = quality (ratings) × output (jobs &amp; hours)
          </p>
        </div>
        <button
          onClick={load}
          className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-emerald-300 hover:bg-emerald-400"
          data-testid="leaderboard-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "REFRESHING…" : "REFRESH"}
        </button>
      </div>

      {/* Filters */}
      <div className="brut-card p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <div className="label-mono mb-1">SEARCH</div>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name or vendor…"
            className="brut-input w-full px-2 py-1.5 text-sm font-mono"
            data-testid="leaderboard-search"
          />
        </div>
        <div>
          <div className="label-mono mb-1">ROLE</div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="brut-input w-full px-2 py-1.5 text-sm font-mono"
            data-testid="leaderboard-role-filter"
          >
            <option value="">All roles</option>
            {roles.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <div className="label-mono mb-1">TEAM</div>
          <select
            value={filterVendor}
            onChange={(e) => setFilterVendor(e.target.value)}
            className="brut-input w-full px-2 py-1.5 text-sm font-mono"
            data-testid="leaderboard-vendor-filter"
          >
            <option value="">All teams</option>
            {vendorOptions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          {(filterRole || filterVendor || q) && (
            <button
              onClick={() => { setFilterRole(""); setFilterVendor(""); setQ(""); }}
              className="brut-btn-secondary px-3 py-1.5 text-xs"
              data-testid="leaderboard-clear-filters"
            >
              CLEAR
            </button>
          )}
          <div className="ml-auto label-mono text-xs text-zinc-500 self-end">{filtered.length} / {rows.length} people</div>
        </div>
      </div>

      {/* Top-3 podium */}
      {!loading && filtered.length >= 3 && (
        <div className="grid grid-cols-3 gap-3" data-testid="leaderboard-podium">
          {filtered.slice(0, 3).map((u, i) => <PodiumTile key={u.user_id} u={u} place={i + 1} />)}
        </div>
      )}

      {/* Table */}
      {loading && <div className="font-mono text-xs text-zinc-500">Loading leaderboard…</div>}
      {!loading && filtered.length === 0 && (
        <div className="brut-card p-10 text-center text-zinc-500 font-mono text-sm">
          NO RESULTS — TRY DIFFERENT FILTERS OR CLICK <strong>REFRESH</strong>.
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="brut-card overflow-x-auto">
          <table className="w-full text-xs font-mono" data-testid="leaderboard-table">
            <thead className="bg-zinc-50 border-b-2 border-zinc-900">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">NAME</th>
                <th className="px-3 py-2 text-left">ROLE</th>
                <th className="px-3 py-2 text-left">TEAM</th>
                <th className="px-3 py-2 text-right">SCORE</th>
                <th className="px-3 py-2 text-right">⭐ RATING</th>
                <th className="px-3 py-2 text-right">JOBS</th>
                <th className="px-3 py-2 text-right">LIVE HRS</th>
                <th className="px-3 py-2 text-right">AVG HRS/JOB</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => <Row key={u.user_id} u={u} />)}
            </tbody>
          </table>
        </div>
      )}

      {/* Team totals */}
      {vendors.length > 0 && (
        <div className="brut-card p-4" data-testid="leaderboard-vendors">
          <div className="label-mono text-zinc-500 mb-2 flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5" /> TEAM TOTALS
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {vendors.map((v) => <VendorTile key={v.vendor_id || v.name} v={v} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function PodiumTile({ u, place }) {
  const tone = place === 1 ? "bg-yellow-200" : place === 2 ? "bg-zinc-200" : "bg-amber-100";
  return (
    <div className={`brut-card p-4 ${tone}`} data-testid={`podium-${place}`}>
      <div className="flex items-center gap-2 mb-2">
        <Trophy className={`w-5 h-5 ${place === 1 ? "text-yellow-700" : place === 2 ? "text-zinc-600" : "text-amber-700"}`} />
        <div className="font-heading text-lg font-black">#{place}</div>
      </div>
      <div className="font-bold text-base truncate" title={u.name}>{u.name}</div>
      <div className="text-[10px] font-mono text-zinc-600 truncate">{u.vendor_name || "—"}</div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-center">
        <div>
          <div className="label-mono text-zinc-500">SCORE</div>
          <div className="font-heading text-xl font-black">{(u.score ?? 0).toFixed(1)}</div>
        </div>
        <div>
          <div className="label-mono text-zinc-500">⭐</div>
          <div className="font-heading text-xl font-black">{(u.avg_rating ?? 0).toFixed(1)}</div>
        </div>
      </div>
    </div>
  );
}

function Row({ u }) {
  const role = (u.role || "").toLowerCase().replace(/\d+$/, "");
  const Icon = ROLE_ICON[role] || UserCog;
  const tone = ROLE_TONE[role] || "bg-zinc-100";
  return (
    <tr className="border-b border-zinc-100 hover:bg-yellow-50" data-testid={`leaderboard-row-${u.user_id}`}>
      <td className="px-3 py-2 font-heading font-black">{u.rank ?? "—"}</td>
      <td className="px-3 py-2">
        <div className="font-bold">{u.name}</div>
        {u.company && <div className="text-[10px] text-zinc-500 truncate max-w-[200px]" title={u.company}>{u.company}</div>}
      </td>
      <td className="px-3 py-2">
        <span className={`px-1.5 py-0.5 border border-zinc-900 ${tone} text-[10px] inline-flex items-center gap-1`}>
          <Icon className="w-3 h-3" /> {(u.role || "—").toUpperCase()}
        </span>
      </td>
      <td className="px-3 py-2 truncate max-w-[200px]" title={u.vendor_name}>{u.vendor_name || "—"}</td>
      <td className="px-3 py-2 text-right font-bold">{(u.score ?? 0).toFixed(1)}</td>
      <td className="px-3 py-2 text-right">
        <span className="inline-flex items-center gap-1">
          <Star className="w-3 h-3 text-yellow-600" />
          {u.total_ratings ? `${(u.avg_rating ?? 0).toFixed(1)} (${u.total_ratings})` : "—"}
        </span>
      </td>
      <td className="px-3 py-2 text-right">{u.jobs_completed ?? 0}</td>
      <td className="px-3 py-2 text-right">
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3 text-zinc-500" />
          {(u.live_hours ?? 0).toFixed(1)}
        </span>
      </td>
      <td className="px-3 py-2 text-right text-zinc-600">{(u.avg_duration_hours ?? 0).toFixed(2)}</td>
    </tr>
  );
}

function VendorTile({ v }) {
  return (
    <div className="border border-zinc-900 p-3" data-testid={`vendor-tile-${v.vendor_id || v.name}`}>
      <div className="font-bold text-sm truncate" title={v.name}>{v.name || "—"}</div>
      <div className="grid grid-cols-3 gap-2 mt-2 text-center">
        <Stat label="MEMBERS" value={v.members ?? v.team_size ?? "—"} />
        <Stat label="JOBS" value={(v.jobs_completed ?? v.total_jobs ?? 0).toLocaleString()} />
        <Stat label="SCORE" value={(v.score ?? 0).toFixed(1)} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="label-mono text-zinc-500 text-[9px]">{label}</div>
      <div className="font-bold text-sm">{value}</div>
    </div>
  );
}
